import { useRef, useState } from "react";
import type { Rfq, RfqInvitation } from "../../domain/rfq-types";
import type { ProcurementScope } from "../shared/useProcurementScope";
import { createRfqInvitation, runRfqInvitationCommand, readRfqTestPortalLink } from "../../infrastructure/rfqs/rfq-invitation-gateway";
import { invitationWriteErrorMessage, rfqUiMessage } from "./rfq-presentation";
import { useRfqReadScope } from "./useRfqReadScope";

type Props = {
  scope: ProcurementScope;
  rfq: Rfq | null;
  canManageInvitations: boolean;
  preview: boolean;
  supplierId: string | null;
  invitations: RfqInvitation[];
  invitationsLoaded: boolean;
  loadInvitations: (rfqId: string) => Promise<boolean>;
  setInvitationError: (message: string) => void;
  onCommandSettled: () => void;
};

export function useRfqInvitationCommands({ scope, rfq, canManageInvitations, preview, supplierId, invitations, invitationsLoaded, loadInvitations, setInvitationError, onCommandSettled }: Props) {
  const recordScope = useRfqReadScope(scope, rfq?.id);
  const viewScope = useRfqReadScope(scope, JSON.stringify([rfq?.id, supplierId]));
  const eligibility = useRef({ invitationsLoaded, canManageInvitations });
  eligibility.current = { invitationsLoaded, canManageInvitations };
  const epoch = useRef(0);
  const lock = useRef<{ scope: typeof recordScope; token: object } | null>(null);
  const [pending, setPending] = useState<{ scope: typeof recordScope; id: string; test: boolean } | null>(null);
  const [feedback, setFeedback] = useState({ scope: viewScope, invitationMessage: "", testPortalUrl: "" });
  const visible = feedback.scope === viewScope ? feedback : { invitationMessage: "", testPortalUrl: "" };
  const clearInvitationFeedback = () => {
    if (!viewScope.isCurrent()) return;
    epoch.current++;
    setFeedback({ scope: viewScope, invitationMessage: "", testPortalUrl: "" });
    setInvitationError("");
  };
  const start = (id: string, test: boolean) => {
    if (!recordScope.isCurrent() || !viewScope.isCurrent() || !rfq || !supplierId || !eligibility.current.canManageInvitations || !eligibility.current.invitationsLoaded || lock.current?.scope === recordScope) return null;
    const token = {};
    lock.current = { scope: recordScope, token };
    clearInvitationFeedback();
    const revision = epoch.current;
    setPending({ scope: recordScope, id, test });
    return { token, isVisible: () => viewScope.isCurrent() && epoch.current === revision };
  };
  const finish = (operation: { token: object }) => {
    if (lock.current?.token !== operation.token) return;
    lock.current = null;
    if (recordScope.isCurrent()) setPending(null);
  };
  const capturePortalUrl = async (invitationId: string) => {
    const portalUrl = await readRfqTestPortalLink(invitationId);
    return new URL(portalUrl, window.location.origin).toString();
  };
  const report = (operation: { isVisible: () => boolean }, message: string, problems: string[], testPortalUrl = "") => {
    if (!operation.isVisible()) return;
    setFeedback({ scope: viewScope, invitationMessage: message, testPortalUrl });
    setInvitationError(problems.join(" "));
    scope.notify([message, ...problems].join(" "));
  };
  const refresh = async () => {
    if (!recordScope.isCurrent() || !rfq) return false;
    try { return await loadInvitations(rfq.id); } catch { return false; }
  };
  const invite = async (targetSupplierId: string) => {
    if (targetSupplierId !== supplierId || !rfq) return;
    const operation = start(targetSupplierId, false);
    if (!operation) return;
    try {
      const invitationId = await createRfqInvitation(rfq.id, targetSupplierId);
      if (!recordScope.isCurrent()) return;
      let message = "Davet güvenli e-posta gönderim kuyruğuna alındı.";
      let testPortalUrl = "";
      const problems: string[] = [];
      if (preview && operation.isVisible()) {
        try {
          testPortalUrl = await capturePortalUrl(invitationId);
          message = "Test e-postası hazırlandı. Tedarikçi panelini aşağıdaki bağlantıdan açabilirsiniz.";
        } catch {
          problems.push("Davet oluşturuldu, ancak test bağlantısı hazırlanamadı. Bağlantıyı yeniden oluşturabilirsiniz.");
        }
      }
      const refreshed = await refresh();
      if (!refreshed) problems.push("Davet listesi yenilenemedi. Listeyi yenileyin; daveti tekrar göndermeyin.");
      report(operation, message, problems, testPortalUrl);
    } catch (value) {
      if (operation.isVisible()) setInvitationError(invitationWriteErrorMessage(value));
    } finally { finish(operation); }
  };
  const invitationCommand = async (invitationId: string, action: "resend" | "revoke") => {
    const target = invitations.find(item => item.id === invitationId && (item.supplierId ?? item.supplier_id) === supplierId);
    if (!target || !rfq) return;
    const operation = start(invitationId, false);
    if (!operation) return;
    try {
      await runRfqInvitationCommand(rfq.id, invitationId, action);
      if (!recordScope.isCurrent()) return;
      const refreshed = await refresh();
      const message = action === "resend" ? "Eski bağlantı iptal edildi; yeni davet gönderim kuyruğuna alındı." : "Davet bağlantısı güvenle iptal edildi.";
      report(operation, message, refreshed ? [] : ["Davet işlemi tamamlandı, ancak liste yenilenemedi. Listeyi yenileyin; işlemi tekrar göndermeyin."]);
    } catch (value) {
      if (operation.isVisible()) setInvitationError(invitationWriteErrorMessage(value));
    } finally {
      finish(operation);
      if (operation.isVisible()) onCommandSettled();
    }
  };
  const loadTestPortalUrl = async (invitationId: string) => {
    if (!preview || !invitations.some(item => item.id === invitationId && item.status !== "REVOKED" && (item.supplierId ?? item.supplier_id) === supplierId)) return;
    const operation = start(invitationId, true);
    if (!operation) return;
    try {
      const testPortalUrl = await capturePortalUrl(invitationId);
      const refreshed = await refresh();
      if (operation.isVisible()) {
        setFeedback({ scope: viewScope, invitationMessage: "", testPortalUrl });
        if (!refreshed) setInvitationError("Test bağlantısı hazır, ancak davet listesi yenilenemedi. Listeyi yenileyin.");
      }
    } catch (value) {
      if (operation.isVisible()) setInvitationError(rfqUiMessage(value, "Test tedarikçi bağlantısı oluşturulamadı."));
    } finally { finish(operation); }
  };
  const busy = pending?.scope === recordScope ? pending : null;
  return { ...visible, invitationPending: busy && !busy.test ? busy.id : "", testDeliveryLoading: busy?.test === true,
    invite, invitationCommand, loadTestPortalUrl, clearInvitationFeedback };
}
